/**
 * Brand Navigation Utilities
 *
 * Centralized logic for brand-only and brand+category navigation
 * from the brands explorer page to the results page.
 */

import { v4 as uuidv4 } from "uuid";

// Storage key generators
export const STORAGE_KEYS = {
  navbarCategory: (id: string) => `navbar-category-${id}`,
  brandFilter: (id: string) => `brand-filter-${id}`,
  taxonomy: (id: string) => `taxonomy-${id}`,
};

/**
 * Data stored in sessionStorage for brand-filter navigation.
 * Used to pass brand (and optional category) filter info to results page.
 */
export interface BrandFilterData {
  /** Brand name to filter by */
  brand: string;
  /** Optional category facet for brand + category navigation */
  categoryFacet?: {
    /** Facet name, e.g., "category_path.depth_2" */
    name: string;
    /** Category value, e.g., "Tops" */
    value: string;
  };
  /** Marker to distinguish from taxonomy navigation (prevents brand name collision with taxonomy roots) */
  _isBrandNavigation: true;
}

/**
 * Prepare sessionStorage for brand + category navigation.
 * Use this when navigating to show products for a brand within a specific category.
 *
 * @param brandName - The brand name to filter by
 * @param categoryName - The category name to filter by
 * @param categoryLevel - The depth level of the category (for facet name)
 * @returns The generated thread ID for navigation
 */
export function prepareBrandCategoryNavigation(
  brandName: string,
  categoryName: string,
  categoryLevel: number
): string {
  const threadId = uuidv4();

  // Set navbar category to empty to prevent highlighting
  sessionStorage.setItem(STORAGE_KEYS.navbarCategory(threadId), "");

  // Store brand + category filter data
  const filterData: BrandFilterData = {
    brand: brandName,
    categoryFacet: {
      name: `category_path.depth_${categoryLevel}`,
      value: categoryName,
    },
    _isBrandNavigation: true,
  };
  sessionStorage.setItem(STORAGE_KEYS.brandFilter(threadId), JSON.stringify(filterData));

  return threadId;
}

/**
 * Read and consume brand filter data from sessionStorage.
 * Returns null if no brand filter data exists for the given thread ID.
 *
 * @param threadId - The thread ID to read data for
 * @returns The brand filter data, or null if not found
 */
export function consumeBrandFilterData(threadId: string): BrandFilterData | null {
  const key = STORAGE_KEYS.brandFilter(threadId);
  const dataStr = sessionStorage.getItem(key);

  if (!dataStr) {
    return null;
  }

  sessionStorage.removeItem(key);

  try {
    const data = JSON.parse(dataStr);
    // Validate the marker to ensure this is brand navigation
    if (data._isBrandNavigation === true) {
      return data as BrandFilterData;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Check if a path segment represents brand navigation vs. taxonomy navigation.
 * Uses the _isBrandNavigation marker for reliable detection.
 *
 * @deprecated Use consumeBrandFilterData instead for reliable detection
 */
export function isBrandNavigationPath(firstSegment: string): boolean {
  // This is a fallback heuristic - prefer using the _isBrandNavigation marker
  const TAXONOMY_ROOTS = ["Menswear", "Womenswear", "Kidswear", "Beauty", "Adult"];
  return !TAXONOMY_ROOTS.includes(firstSegment);
}
